import React, { useState, useEffect, useCallback } from "react";
import {
  FaPlus,
  FaEdit,
  FaTrash,
  FaTimes,
  FaTags,
  FaSearch,
  FaSpinner,
} from "react-icons/fa";
import apiRequest from "../services/api";

const ExpenseCategoryManagement = () => {
  const [categories, setCategories] = useState([]);
  const [keyword, setKeyword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState("");

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState("create");
  const [formData, setFormData] = useState({ id: null, name: "", rent: false });

  const fetchCategories = useCallback(async () => {
    setIsLoading(true);
    setError("");

    try {
      const resp = await apiRequest("expenses/categories");
      const data = resp?.data || [];

      setCategories(
        keyword.trim()
          ? data.filter((cat) =>
              cat.name?.toLowerCase().includes(keyword.toLowerCase())
            )
          : data
      );
    } catch (err) {
      setError(err?.message || "Unable to load expense categories.");
      setCategories([]);
    } finally {
      setIsLoading(false);
    }
  }, [keyword]);

  useEffect(() => {
    fetchCategories();
  }, [fetchCategories]);

  const openModal = (mode, category = null) => {
    setModalMode(mode);
    setError("");

    setFormData(
      category
        ? {
            id: category.id,
            name: category.name,
            rent: Boolean(category.rent),
          }
        : { id: null, name: "", rent: false }
    );

    setIsModalOpen(true);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setError("");

    if (!formData.name.trim()) {
      setError("Category name is required.");
      return;
    }

    const method = modalMode === "create" ? "POST" : "PUT";

    try {
      await apiRequest("expenses/categories", {
        method,
        body: formData, // includes rent: true/false
      });

      setIsModalOpen(false);
      await fetchCategories();
    } catch (err) {
      setError(err?.message || "Unable to save expense category.");
    }
  };

  const handleDelete = async (id, name) => {
    if (
      !window.confirm(
        `Are you sure you want to delete expense category "${name}"?`
      )
    ) {
      return;
    }

    setError("");

    try {
      await apiRequest(`expenses/categories/${id}`, {
        method: "DELETE",
      });

      await fetchCategories();
    } catch (err) {
      setError(err?.message || "Unable to delete expense category.");
    }
  };

  return (
    <div
      className="
        min-h-full p-4 md:p-8 font-sans
        bg-slate-50 text-slate-800
        dark:bg-slate-950 dark:text-slate-100
      "
    >
      <div className="max-w-7xl mx-auto space-y-6">
        {/* HEADER */}
        <div
          className="
            flex flex-col md:flex-row md:items-center justify-between
            gap-4 p-5 rounded-2xl shadow-sm border backdrop-blur-md
            bg-white border-slate-200
            dark:bg-slate-900/40 dark:border-white/10 dark:shadow-xl
          "
        >
          <div className="flex items-center gap-3.5">
            <div
              className="
                p-3 rounded-xl shadow-inner border
                bg-gradient-to-br from-emerald-100 to-teal-50
                border-emerald-200 text-emerald-600
                dark:from-emerald-500/20 dark:to-teal-500/10
                dark:border-emerald-500/30 dark:text-emerald-400
              "
            >
              <FaTags className="text-2xl" />
            </div>

            <div>
              <h1
                className="
                  text-xl font-black tracking-tight uppercase
                  text-slate-900 dark:text-white
                "
              >
                Expense Categories
              </h1>

              <p className="text-xs mt-0.5 text-slate-500 dark:text-slate-400">
                <span className="font-mono font-semibold text-emerald-600 dark:text-emerald-400">
                  {categories.length}
                </span>{" "}
                expense categories configured
              </p>
            </div>
          </div>

          <button
            type="button"
            onClick={() => openModal("create")}
            className="
              flex items-center justify-center gap-2
              px-4 py-2.5 rounded-xl text-xs font-semibold
              transition-all shadow-sm
              bg-emerald-600 text-white
              hover:bg-emerald-700
              dark:bg-emerald-600 dark:hover:bg-emerald-500
            "
          >
            <FaPlus />
            New Category
          </button>
        </div>

        {/* SEARCH */}
        <div
          className="
            grid grid-cols-1 gap-3 p-3 rounded-xl border backdrop-blur-sm
            bg-white border-slate-200
            dark:bg-slate-900/20 dark:border-white/5
          "
        >
          <div className="relative">
            <span
              className="
                absolute inset-y-0 left-0 pl-3
                flex items-center pointer-events-none
                text-slate-400 dark:text-slate-500
              "
            >
              <FaSearch className="text-xs" />
            </span>

            <input
              type="text"
              value={keyword}
              onChange={(e) => setKeyword(e.target.value)}
              placeholder="Search expense categories..."
              className="
                w-full rounded-xl pl-9 pr-4 py-2.5 text-xs
                transition focus:outline-none focus:border-emerald-500/40
                bg-slate-50 border border-slate-200
                text-slate-800 placeholder-slate-400
                dark:bg-slate-950/60 dark:border-white/10
                dark:text-white dark:placeholder-slate-600
              "
            />
          </div>
        </div>

        {/* ERROR */}
        {error && (
          <div
            className="
              p-4 rounded-xl border text-xs font-medium
              bg-red-50 border-red-200 text-red-600
              dark:bg-red-500/10 dark:border-red-500/20 dark:text-red-400
            "
          >
            {error}
          </div>
        )}

        {/* TABLE */}
        <div
          className="
            border rounded-2xl shadow-sm backdrop-blur-md
            bg-white border-slate-200
            dark:bg-slate-900/30 dark:border-white/10 dark:shadow-2xl
          "
        >
          <div className="overflow-x-auto rounded-2xl">
            <table className="w-full text-left border-collapse min-w-[600px]">
              <thead>
                <tr
                  className="
                    border-b text-[10px] font-bold tracking-wider uppercase
                    bg-slate-50 border-slate-200 text-slate-500
                    dark:bg-slate-950/50 dark:border-white/10
                    dark:text-slate-400
                  "
                >
                  <th className="px-5 py-4"> Name</th>
                  <th className="px-5 py-4">Is Rent?</th>
                  <th className="px-5 py-4 text-right">Actions</th>
                </tr>
              </thead>

              <tbody
                className="
                  divide-y font-mono text-xs
                  divide-slate-100 text-slate-600
                  dark:divide-white/5 dark:text-slate-300
                "
              >
                {categories.length === 0 && !isLoading ? (
                  <tr>
                    <td
                      colSpan={3}
                      className="
                        text-center py-12
                        text-slate-500 font-sans italic
                        dark:text-slate-500
                      "
                    >
                      No expense categories found.
                    </td>
                  </tr>
                ) : (
                  categories.map((cat) => (
                    <tr
                      key={cat.id}
                      className="
                        group transition
                        hover:bg-slate-50
                        dark:hover:bg-white/[0.02]
                      "
                    >
                      <td className="px-5 py-3.5 font-sans">
                        <div
                          className="
                            font-semibold
                            text-slate-800 dark:text-slate-200
                          "
                        >
                          {cat.name}
                        </div>
                      </td>

                      <td className="px-5 py-3.5">
                        <span
                          className={`
                            inline-flex items-center px-2.5 py-0.5 rounded-full
                            text-[10px] font-semibold uppercase tracking-wide
                            ${
                              cat.rent
                                ? "bg-emerald-100 text-emerald-700 dark:bg-emerald-500/20 dark:text-emerald-400"
                                : "bg-slate-100 text-slate-500 dark:bg-slate-700/50 dark:text-slate-400"
                            }
                          `}
                        >
                          {cat.rent ? "Yes" : "No"}
                        </span>
                      </td>

                      <td className="px-5 py-3.5">
                        <div className="flex justify-end gap-2">
                          <button
                            type="button"
                            onClick={() => openModal("edit", cat)}
                            title="Edit category"
                            className="
                              p-2 rounded-lg border transition
                              bg-emerald-50 border-emerald-200
                              text-emerald-600 hover:bg-emerald-100
                              dark:bg-emerald-500/10
                              dark:border-emerald-500/10
                              dark:text-emerald-400
                              dark:hover:bg-emerald-500/20
                            "
                          >
                            <FaEdit className="text-xs" />
                          </button>

                          <button
                            type="button"
                            onClick={() => handleDelete(cat.id, cat.name)}
                            title="Delete category"
                            className="
                              p-2 rounded-lg border transition
                              bg-red-50 border-red-200
                              text-red-600 hover:bg-red-100
                              dark:bg-red-500/10
                              dark:border-red-500/10
                              dark:text-red-400
                              dark:hover:bg-red-500/20
                            "
                          >
                            <FaTrash className="text-xs" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* LOADING */}
          {isLoading && (
            <div
              className="
                p-8 flex items-center justify-center gap-3 text-xs border-t
                bg-slate-50 text-slate-500 border-slate-200
                dark:bg-slate-950/20 dark:text-slate-400
                dark:border-white/5
              "
            >
              <FaSpinner className="animate-spin text-emerald-500 text-base" />
              <span>Loading expense categories...</span>
            </div>
          )}
        </div>

        {/* BOTTOM SHEET MODAL */}
        <div
          className={`
            fixed inset-0 z-[2000] flex flex-col justify-end
            transition-opacity duration-300
            ${
              isModalOpen
                ? "opacity-100 pointer-events-auto"
                : "opacity-0 pointer-events-none"
            }
          `}
        >
          {/* BACKDROP */}
          <div
            className="
              absolute inset-0
              bg-slate-950/60 backdrop-blur-sm
            "
            onClick={() => setIsModalOpen(false)}
          />

          {/* SHEET */}
          <div
            className={`
              relative w-full max-w-md mx-auto
              rounded-t-3xl p-6 shadow-2xl
              border
              bg-white border-slate-200
              dark:bg-slate-900 dark:border-white/10
              transition-transform duration-300 transform
              ${isModalOpen ? "translate-y-0" : "translate-y-full"}
            `}
          >
            {/* MODAL HEADER */}
            <div
              className="
                flex justify-between items-center
                pb-3 mb-4 border-b
                border-slate-200 dark:border-white/5
              "
            >
              <div className="flex items-center gap-2">
                <div
                  className="
                    p-2 rounded-lg
                    bg-emerald-50 text-emerald-600
                    dark:bg-emerald-500/10 dark:text-emerald-400
                  "
                >
                  <FaTags className="text-xs" />
                </div>

                <h3
                  className="
                    text-sm font-bold capitalize
                    text-slate-900 dark:text-white
                  "
                >
                  {modalMode} Expense Category
                </h3>
              </div>

              <button
                type="button"
                onClick={() => setIsModalOpen(false)}
                className="
                  p-2 rounded-lg transition
                  text-slate-400 hover:text-slate-700
                  dark:text-slate-400 dark:hover:text-white
                  hover:bg-slate-100 dark:hover:bg-white/5
                "
              >
                <FaTimes />
              </button>
            </div>

            {/* FORM */}
            <form onSubmit={handleSave} className="space-y-4">
              <div className="flex flex-col gap-1.5">
                <label
                  className="
                    text-[10px] uppercase font-bold tracking-wider
                    text-slate-500 dark:text-slate-400
                  "
                >
                  Category Name
                </label>

                <input
                  type="text"
                  required
                  autoFocus
                  placeholder="e.g. Utilities, Logistics, Rent"
                  value={formData.name}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      name: e.target.value,
                    })
                  }
                  className="
                    w-full rounded-xl px-4 py-2.5 text-xs
                    focus:outline-none focus:border-emerald-500/50
                    bg-slate-50 border border-slate-200
                    text-slate-800 placeholder-slate-400
                    dark:bg-slate-950/60 dark:border-white/10
                    dark:text-white dark:placeholder-slate-600
                  "
                />
              </div>

              {/* RENT TOGGLE */}
              <div className="flex items-center justify-between gap-3 pt-1">
                <div>
                  <label
                    className="
                      text-[10px] uppercase font-bold tracking-wider
                      text-slate-500 dark:text-slate-400
                    "
                  >
                    Rent Category
                  </label>
                  <p className="text-[11px] text-slate-400 dark:text-slate-500 mt-0.5">
                    Mark this category as rent-related
                  </p>
                </div>

                <button
                  type="button"
                  role="switch"
                  aria-checked={formData.rent}
                  onClick={() =>
                    setFormData((prev) => ({
                      ...prev,
                      rent: !prev.rent,
                    }))
                  }
                  className={`
                    relative inline-flex h-6 w-11 shrink-0 cursor-pointer
                    rounded-full border-2 border-transparent transition-colors
                    focus:outline-none focus:ring-2 focus:ring-emerald-500/40
                    ${
                      formData.rent
                        ? "bg-emerald-600"
                        : "bg-slate-200 dark:bg-slate-700"
                    }
                  `}
                >
                  <span
                    className={`
                      pointer-events-none inline-block h-5 w-5 transform
                      rounded-full bg-white shadow ring-0 transition
                      ${formData.rent ? "translate-x-5" : "translate-x-0"}
                    `}
                  />
                </button>
              </div>

              {/* BUTTONS */}
              <div className="flex justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="
                    px-4 py-2 rounded-xl text-xs font-semibold
                    bg-slate-100 text-slate-600
                    hover:bg-slate-200
                    dark:bg-white/5 dark:text-slate-300
                    dark:hover:bg-white/10
                  "
                >
                  Cancel
                </button>

                <button
                  type="submit"
                  className="
                    px-5 py-2 rounded-xl
                    bg-emerald-600 text-white
                    hover:bg-emerald-700
                    dark:bg-emerald-600 dark:hover:bg-emerald-500
                    text-xs font-semibold transition
                  "
                >
                  {modalMode === "create" ? "Create Category" : "Save Changes"}
                </button>
              </div>
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ExpenseCategoryManagement;
